<template>
  <q-btn-dropdown
    v-bind="dropdownProps"
  >
    <template #label>
      <span>
        <q-icon
          v-if="useLinkStyle || hideLabel"
          name="add"
          :class="{ 'icon-margin': useLinkStyle }"
        />
        <span v-if="!hideLabel">
          <slot name="label">
            {{ $t('programs.buttonAddProgram') }}
          </slot>
        </span>
      </span>
    </template>
    <q-list
      data-e2e="list-templates-programs"
    >
      <q-input
        v-if="!hideSearch"
        v-model="searchQuery"
        debounce="500"
        :label="$t('programs.inputSearch')"
        dense
        autofocus
        hide-bottom-space
        outlined
        clearable
        @update:model-value="onSearchInput"
      >
        <template
          v-if="isSearchInProgress"
          #append
        >
          <q-spinner />
        </template>
      </q-input>
      <template v-if="isSearching">
        <q-item
          dense
          class="items-center justify-center bg-primary text-white text-bold"
        >
          <q-item-section>
            <q-item-label color="dark">
              {{ $t('programs.headingSearchResults') }}
            </q-item-label>
          </q-item-section>
        </q-item>
        <template v-if="!hasSearchResults && !isSearchInProgress">
          <q-item
            class="items-center"
          >
            <q-item-section>
              <q-item-label color="dark">
                {{ $t('programs.textNoSearchResults') }}
              </q-item-label>
            </q-item-section>
          </q-item>
        </template>
        <template v-else>
          <q-item
            v-for="searchResult in searchResults"
            :key="searchResult.id"
            v-ripple
            clickable
            class="items-center"
          >
            <q-item-section
              v-close-popup
              @click="onUseSearchResultProgram(searchResult.id)"
            >
              <q-item-label color="dark">
                {{ searchResult.programName }}
              </q-item-label>
            </q-item-section>
            <q-item-section
              avatar
            >
              <q-btn
                flat
                round
                dense
                color="primary"
                icon="preview"
                @click="onPreviewSearchResult(searchResult)"
              >
                <q-tooltip>
                  {{ $t('programs.listItemPreview') }}
                </q-tooltip>
              </q-btn>
            </q-item-section>
          </q-item>
        </template>
      </template>
      <template v-else-if="hasBothClientTypes">
        <hr-menu-item
          v-for="clientType in programClientTypes"
          :key="clientType"
          :label="$t(clientTypeLabelKeys[clientType])"
          :data-e2e="`list-item-add-program-${clientType}`"
        >
          <template #trailing>
            <hr-icon
              name="chevron_right"
              size="sm"
            />
          </template>
          <template #popup>
            <hr-menu
              :anchor="isNarrowScreen ? 'bottom middle' : 'top end'"
              :self="isNarrowScreen ? 'top middle' : 'top start'"
              :class="{ 'add-program-button__submenu--narrow': isNarrowScreen }"
            >
              <hr-list>
                <add-program-menu-sections
                  v-bind="sectionProps(clientType)"
                  @add-program="onAddProgram"
                  @use-program="onUseTemplateProgram"
                />
              </hr-list>
            </hr-menu>
          </template>
        </hr-menu-item>
      </template>
      <template v-else>
        <add-program-menu-sections
          v-for="clientType in programClientTypes"
          :key="clientType"
          v-bind="sectionProps(clientType)"
          @add-program="onAddProgram"
          @use-program="onUseTemplateProgram"
        />
      </template>
    </q-list>
  </q-btn-dropdown>
</template>
<script>
import AddProgramMenuSections from 'components/Programs/AddProgramMenuSections.vue';
import ProgramInfo from 'components/Programs/ProgramInfo';
import { isEmpty, trim } from 'lodash';
import { mapActions, mapState } from 'pinia';
import { callFirebaseFunction } from 'src/boot/firebase';
import { useBehaviorTrackingFeature } from 'src/composables/analytics/useBehaviorTrackingFeature';
import { useAuth } from 'src/composables/auth';
import {
  HrIcon, HrList, HrMenu, HrMenuItem,
} from 'src/design-system';
import ProgramsDB from 'src/fb/programs-db';
import Interactions from 'src/mixins/Interactions';
import Toolbox from 'src/mixins/Toolbox';
import { auth } from 'src/store/auth';
import { network } from 'src/store/network';
import { programs } from 'src/store/programs';

export default {
  name: 'AddProgramButton',

  components: {
    AddProgramMenuSections, HrIcon, HrList, HrMenu, HrMenuItem,
  },

  mixins: [Interactions, Toolbox],

  props: {
    disableBehaviorProgram: {
      type: Boolean,
      default: false,
    },

    hideLabel: {
      type: Boolean,
      default: false,
    },

    hideSearch: {
      type: Boolean,
      default: false,
    },

    hideTemplates: {
      type: Boolean,
      default: false,
    },

    includeCatalogOnlyProgramTypes: {
      type: Boolean,
      default: false,
    },

    useLinkStyle: {
      type: Boolean,
      default: false,
    },

    programClientTypes: {
      type: Array,
      default: () => ['client', 'staff'],
    },
  },

  emits: ['add-program', 'use-program'],

  setup() {
    const { behaviorTrackingEnabled } = useBehaviorTrackingFeature();
    const { userHasPrivilege } = useAuth();

    return { behaviorTrackingEnabled, userHasPrivilege };
  },

  data() {
    return {
      clientTypeLabelKeys: {
        client: 'programs.listItemClient',
        staff: 'programs.listItemStaff',
      },
      searchQuery: '',
      isSearchInProgress: false,
      searchResults: [],
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'userIsAdmin']),
    ...mapState(programs, ['programTemplates', 'isLoadingProgramTemplates']),
    ...mapState(network, ['offlineMode']),

    dropdownProps() {
      return {
        'menu-self': this.useLinkStyle ? 'top left' : 'top end',
        unelevated: this.useLinkStyle,
        square: this.useLinkStyle,
        align: this.useLinkStyle ? 'between' : 'center',
        'no-caps': this.useLinkStyle,
        disable: this.offlineMode,
        color: this.useLinkStyle ? undefined : 'primary',
        class: { 'q-btn-hide-dropdown-arrow': this.useLinkStyle },
      };
    },

    isSearching() {
      return ((trim(this.searchQuery) || '') !== '');
    },

    hasSearchResults() {
      return !isEmpty(this.searchResults);
    },

    hasClientPrograms() {
      return this.programClientTypes.includes('client');
    },

    hasStaffPrograms() {
      return this.programClientTypes.includes('staff');
    },

    hasBothClientTypes() {
      return this.hasClientPrograms && this.hasStaffPrograms;
    },

    isNarrowScreen() {
      return this.$q.screen.lt.sm;
    },

    clientTypeForSearchEngine() {
      if (this.hasBothClientTypes) {
        return undefined;
      }
      return this.hasClientPrograms ? 'client' : 'staff';
    },

    sortedProgramTemplates() {
      return this.naturalSort(
        this.programTemplates.filter((program) => (this.programClientTypes.includes(program.clientType ?? 'client'))),
        'programName',
      );
    },
  },

  created() {
    if (!this.offlineMode) {
      this.bindProgramTemplates();
    }
  },

  methods: {
    ...mapActions(programs, ['bindProgramTemplates']),

    onAddProgram(programType, clientType) {
      this.$emit('add-program', programType, clientType);
    },

    onUseSearchResultProgram(programId) {
      this.$emit('use-program', programId, false);
    },

    onUseTemplateProgram(programId) {
      this.$emit('use-program', programId, true);
    },

    sectionProps(clientType) {
      return {
        clientType,
        templates: this.sortedProgramTemplates.filter((program) => (program.clientType ?? 'client') === clientType),
        isLoadingTemplates: this.isLoadingProgramTemplates,
        hideTemplates: this.hideTemplates,
        includeCatalogOnlyProgramTypes: this.includeCatalogOnlyProgramTypes,
        isAdmin: this.userIsAdmin,
        canManageTemplates: this.userHasPrivilege('pages/programTemplates'),
        disableBehaviorProgram: this.disableBehaviorProgram,
        behaviorTrackingEnabled: this.behaviorTrackingEnabled,
      };
    },

    openProgramPreview(program) {
      this.openComponentDialog(ProgramInfo, { program });
    },

    async onPreviewSearchResult(searchResult) {
      this.$q.loading.show();
      const programsDb = new ProgramsDB();
      try {
        const program = await programsDb.read(searchResult.id);
        this.openProgramPreview(program);
      } catch (err) {
        this.notifyNegative(this.$t('programs.notifyPreviewNotFound'));
      } finally {
        this.$q.loading.hide();
      }
    }, // onPreviewSearchResult

    onSearchInput() {
      this.searchPrograms();
    },

    async searchPrograms() {
      if (this.searchQuery) {
        this.isSearchInProgress = true;

        const { data } = await callFirebaseFunction('findLibraryPrograms', {
          query: this.searchQuery,
          clinic: this.userDetails.clinic,
          clientType: this.clientTypeForSearchEngine,
        });

        this.isSearchInProgress = false;
        this.searchResults = data;
      }
    },
  },

};
</script>

<style>
.q-btn-hide-dropdown-arrow .q-btn-dropdown__arrow {
  display: none;
}
.icon-margin {
  margin-right: 32px;
}
.hr-menu.add-program-button__submenu--narrow {
  min-width: 0;
}
</style>
